import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary,
  Alert, AlertTitle,
  Avatar, Box, Button, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, MenuItem, Paper, Stack, TextField,
  Tooltip, Typography,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import {
  CheckCircleOutline as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Schedule as SnoozeIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Visibility as ViewIcon,
  Undo as RollbackIcon,
  Bolt as AutoIcon,
} from '@mui/icons-material';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import { PageHeader, StatCard } from '../../components/ui';
import { getWbImageUrl, MP_COLORS, MP_MAP, MP_TEXT_COLORS } from '../../utils/productImage';

const STATUS_LABELS = {
  pending: 'Ожидает',
  approved: 'Одобрено',
  executing: 'Выполняется',
  executed: 'Выполнено',
  measured: 'Измерено',
  rejected: 'Отклонено',
  failed: 'Ошибка',
  expired: 'Истёк',
};

const STATUS_COLORS = {
  pending: 'warning',
  approved: 'info',
  executing: 'info',
  executed: 'success',
  measured: 'success',
  rejected: 'default',
  failed: 'error',
  expired: 'default',
};

const ACTION_TYPE_LABELS = {
  price_update: 'Изменить цену',
  ad_bid_update: 'Ставка рекламы',
  ad_campaign_pause: 'Пауза кампании',
  review_reply: 'Ответ на отзыв',
  supply_create: 'Создать поставку',
  recipe_save: 'Рецепт в базу',
  lesson_save: 'Урок в базу',
  other: 'Прочее',
};

const ACTION_TYPE_COLORS = {
  price_update: '#1976d2',
  ad_bid_update: '#8e24aa',
  ad_campaign_pause: '#d32f2f',
  review_reply: '#388e3c',
  supply_create: '#f57c00',
  recipe_save: '#0288d1',
  lesson_save: '#0288d1',
  other: '#616161',
};

const PRIORITY_LABELS = { high: 'Высокий', medium: 'Средний', low: 'Низкий' };
const PRIORITY_COLORS = { high: 'error', medium: 'warning', low: 'default' };

const MP_LABELS = { wb: 'WB', ozon: 'Ozon', yandex: 'Yandex', internal: 'Internal' };
const MP_TO_NUM = { wb: 1, ozon: 2, yandex: 3 };

function formatNumber(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
}

function formatCountdown(expiresAt) {
  if (!expiresAt) return '—';
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms)) return '—';
  if (ms <= 0) return 'истёк';
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const minutes = totalMin % 60;
  if (days > 0) return `${days}д ${hours}ч`;
  if (hours > 0) return `${hours}ч ${minutes}м`;
  return `${minutes}м`;
}

function pickSku(payload) {
  if (!payload || typeof payload !== 'object') return null;
  return payload.sku || payload.article || payload.nm_id || payload.product_id || null;
}

function safeStringify(obj) {
  try {
    return JSON.stringify(obj || {}, null, 2);
  } catch (_) {
    return String(obj);
  }
}

function ActionPhoto({ marketplace, payload }) {
  const sku = pickSku(payload);
  let src = null;
  if (marketplace === 'wb' && sku && /^\d+$/.test(String(sku))) {
    src = getWbImageUrl(Number(sku));
  }
  const mpNum = MP_TO_NUM[marketplace];
  if (!src) {
    return (
      <Avatar variant="rounded" sx={{ width: 44, height: 44, bgcolor: 'grey.200', fontSize: 12 }}>
        {sku ? String(sku).slice(-3) : '—'}
      </Avatar>
    );
  }
  return (
    <Box sx={{ position: 'relative' }}>
      <Avatar
        variant="rounded"
        src={src}
        sx={{ width: 44, height: 44, bgcolor: 'grey.100' }}
        slotProps={{ img: { loading: 'lazy', onError: (e) => { e.target.style.display = 'none'; } } }}
      />
      {mpNum && (
        <Chip
          size="small"
          label={MP_MAP[mpNum]}
          sx={{
            position: 'absolute',
            right: -6,
            bottom: -6,
            height: 16,
            fontSize: 9,
            bgcolor: MP_COLORS[mpNum],
            color: MP_TEXT_COLORS[mpNum],
            '& .MuiChip-label': { px: 0.5 },
          }}
        />
      )}
    </Box>
  );
}

function VerifierChip({ status, warnings }) {
  if (!status) return null;
  const color = status === 'approved' ? 'success' : status === 'needs_review' ? 'warning' : 'default';
  const tooltip = (warnings || []).map((w) => `${w.severity}: ${w.description}`).join('\n') || 'verifier ok';
  return (
    <Tooltip title={tooltip} placement="top">
      <Chip size="small" color={color} variant="outlined" label={`verifier: ${status}`} />
    </Tooltip>
  );
}

export default function AiApprovals() {
  const { success, error: showError } = useNotify();
  // Сохраняем notify-функции в ref, чтобы не тащить их в зависимости useCallback —
  // это защищает от бесконечного ререндер-цикла, даже если useNotify когда-то
  // снова начнёт возвращать новые ссылки на каждый рендер.
  const showErrorRef = useRef(showError);
  const successRef = useRef(success);
  useEffect(() => { showErrorRef.current = showError; }, [showError]);
  useEffect(() => { successRef.current = success; }, [success]);

  const [loading, setLoading] = useState(false);
  const [actions, setActions] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ pending: 0, high_priority: 0, expiring_soon: 0 });
  // accessError: { code: 401|403, message } — при auth/permission ошибке
  // прекращаем опрос и показываем понятное сообщение пользователю.
  const [accessError, setAccessError] = useState(null);
  const accessErrorRef = useRef(null);
  useEffect(() => { accessErrorRef.current = accessError; }, [accessError]);

  const [filters, setFilters] = useState({
    status: 'pending',
    marketplace: '',
    priority: '',
    action_type: '',
  });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editPayloadOpen, setEditPayloadOpen] = useState(false);
  const [editPayloadText, setEditPayloadText] = useState('');
  const [editComment, setEditComment] = useState('');
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectComment, setRejectComment] = useState('');
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [snoozeDays, setSnoozeDays] = useState(3);
  const [actionTarget, setActionTarget] = useState(null);

  const handleAuthError = useCallback((e) => {
    const code = e?.response?.status;
    if (code === 401 || code === 403) {
      const message = code === 401
        ? 'Необходима повторная авторизация для доступа к Approval Center.'
        : 'Нет прав на просмотр Approval Center. Запросите права aiApprovals у администратора.';
      // Останавливаем дальнейшие запросы до перезагрузки страницы.
      setAccessError({ code, message });
      return true;
    }
    return false;
  }, []);

  const loadStats = useCallback(async () => {
    if (accessErrorRef.current) return;
    try {
      const { data } = await axios.get('/ai/approvals/stats');
      setStats(data || { pending: 0, high_priority: 0, expiring_soon: 0 });
    } catch (e) {
      handleAuthError(e);
      // 5xx и прочие сетевые ошибки для статистики не критичны — молча игнорим.
    }
  }, [handleAuthError]);

  const loadList = useCallback(async () => {
    if (accessErrorRef.current) return;
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const { data } = await axios.get('/ai/approvals', { params });
      setActions(Array.isArray(data?.actions) ? data.actions : []);
      setTotal(Number(data?.total) || 0);
    } catch (e) {
      if (!handleAuthError(e)) {
        showErrorRef.current?.(e?.response?.data?.message || e.message || 'Не удалось загрузить список');
      }
    } finally {
      setLoading(false);
    }
  }, [filters, handleAuthError]);

  useEffect(() => {
    if (accessError) return;
    loadList();
    loadStats();
  }, [loadList, loadStats, accessError]);

  useEffect(() => {
    if (accessError) return undefined;
    const t = setInterval(() => loadStats(), 60_000);
    return () => clearInterval(t);
  }, [loadStats, accessError]);

  const openDetail = async (row) => {
    setDetail(null);
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const { data } = await axios.get(`/ai/approvals/${row.id}`);
      setDetail(data?.action || row);
    } catch (e) {
      if (!handleAuthError(e)) {
        showErrorRef.current?.(e?.response?.data?.message || 'Не удалось загрузить детали');
      }
      setDetail(row);
    } finally {
      setDetailLoading(false);
    }
  };

  const doApprove = async (row, { payload, comment } = {}) => {
    try {
      const body = {};
      if (payload && typeof payload === 'object') body.payload = payload;
      if (comment) body.comment = comment;
      const { data } = await axios.post(`/ai/approvals/${row.id}/approve`, body);
      successRef.current?.(`Action #${row.id}: ${data?.status || 'approved'}. Executor запущен в фоне.`);
      await loadList();
      await loadStats();
      return true;
    } catch (e) {
      if (!handleAuthError(e)) {
        showErrorRef.current?.(e?.response?.data?.message || e.message || 'Не удалось одобрить');
      }
      return false;
    }
  };

  const doReject = async (row, comment) => {
    try {
      await axios.post(`/ai/approvals/${row.id}/reject`, { comment });
      successRef.current?.(`Action #${row.id} отклонён`);
      await loadList();
      await loadStats();
      return true;
    } catch (e) {
      if (!handleAuthError(e)) {
        showErrorRef.current?.(e?.response?.data?.message || e.message || 'Не удалось отклонить');
      }
      return false;
    }
  };

  const doRollback = async (row) => {
    if (!window.confirm(
      `Откатить action #${row.id}?\n\nДля price_update/ad_bid_update будет отправлен обратный write-вызов в маркетплейс (старая цена/ставка из effect_snapshot_before). ad_campaign_pause выполнит обратную операцию.\n\nЭто действие необратимо.`
    )) return false;
    try {
      const { data } = await axios.post(`/ai/approvals/${row.id}/rollback`);
      successRef.current?.(`Action #${row.id} откачен: ${data?.result?.handler || 'ok'}`);
      await loadList();
      return true;
    } catch (e) {
      if (!handleAuthError(e)) {
        showErrorRef.current?.(e?.response?.data?.message || e.message || 'Не удалось откатить');
      }
      return false;
    }
  };

  const doSnooze = async (row, days) => {
    try {
      const { data } = await axios.post(`/ai/approvals/${row.id}/snooze`, { days });
      successRef.current?.(`Action #${row.id} отложен на ${data?.snoozed_days || days}д`);
      await loadList();
      return true;
    } catch (e) {
      if (!handleAuthError(e)) {
        showErrorRef.current?.(e?.response?.data?.message || e.message || 'Не удалось отложить');
      }
      return false;
    }
  };

  const handleEditApproveOpen = (row) => {
    setActionTarget(row);
    setEditPayloadText(safeStringify(row?.payload || {}));
    setEditComment('');
    setEditPayloadOpen(true);
  };

  const handleEditApproveSubmit = async () => {
    let parsed = null;
    try {
      parsed = JSON.parse(editPayloadText || '{}');
    } catch (e) {
      showErrorRef.current?.('Невалидный JSON в payload: ' + e.message);
      return;
    }
    const ok = await doApprove(actionTarget, { payload: parsed, comment: editComment });
    if (ok) {
      setEditPayloadOpen(false);
      setActionTarget(null);
    }
  };

  const handleRejectOpen = (row) => {
    setActionTarget(row);
    setRejectComment('');
    setRejectOpen(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectComment.trim()) {
      showErrorRef.current?.('Укажите причину отклонения');
      return;
    }
    const ok = await doReject(actionTarget, rejectComment);
    if (ok) {
      setRejectOpen(false);
      setActionTarget(null);
    }
  };

  const handleSnoozeOpen = (row) => {
    setActionTarget(row);
    setSnoozeDays(3);
    setSnoozeOpen(true);
  };

  const handleSnoozeSubmit = async () => {
    const ok = await doSnooze(actionTarget, Number(snoozeDays) || 3);
    if (ok) {
      setSnoozeOpen(false);
      setActionTarget(null);
    }
  };

  const isPending = (row) => row.status === 'pending';

  const tableRows = useMemo(() => actions, [actions]);

  if (accessError) {
    return (
      <>
        <PageHeader
          title="AI Approval Center"
          subtitle="Очередь действий AI-агента, ожидающих ручного подтверждения"
        />
        <Alert
          severity={accessError.code === 401 ? 'warning' : 'error'}
          sx={{ mt: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                setAccessError(null);
                accessErrorRef.current = null;
              }}
            >
              Повторить
            </Button>
          }
        >
          <AlertTitle>{accessError.code === 401 ? 'Не авторизованы' : 'Нет прав доступа'}</AlertTitle>
          {accessError.message}
        </Alert>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="AI Approval Center"
        subtitle="Очередь действий AI-агента, ожидающих ручного подтверждения"
        actions={
          <Button startIcon={<RefreshIcon />} onClick={() => { loadList(); loadStats(); }}>
            Обновить
          </Button>
        }
      />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <StatCard title="В ожидании" value={stats.pending} color="warning" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard title="High priority" value={stats.high_priority} color="error" />
        </Grid>
        <Grid item xs={12} sm={4}>
          <StatCard title="Истекают &lt; 24ч" value={stats.expiring_soon} color="info" />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <TextField
            select size="small" label="Статус" sx={{ minWidth: 150 }}
            value={filters.status}
            onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          >
            <MenuItem value="">Все</MenuItem>
            {Object.entries(STATUS_LABELS).map(([k, l]) => (
              <MenuItem key={k} value={k}>{l}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="МП" sx={{ minWidth: 130 }}
            value={filters.marketplace}
            onChange={(e) => setFilters((f) => ({ ...f, marketplace: e.target.value }))}
          >
            <MenuItem value="">Все</MenuItem>
            {Object.entries(MP_LABELS).map(([k, l]) => (
              <MenuItem key={k} value={k}>{l}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Приоритет" sx={{ minWidth: 150 }}
            value={filters.priority}
            onChange={(e) => setFilters((f) => ({ ...f, priority: e.target.value }))}
          >
            <MenuItem value="">Все</MenuItem>
            {Object.entries(PRIORITY_LABELS).map(([k, l]) => (
              <MenuItem key={k} value={k}>{l}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Тип" sx={{ minWidth: 200 }}
            value={filters.action_type}
            onChange={(e) => setFilters((f) => ({ ...f, action_type: e.target.value }))}
          >
            <MenuItem value="">Все</MenuItem>
            {Object.entries(ACTION_TYPE_LABELS).map(([k, l]) => (
              <MenuItem key={k} value={k}>{l}</MenuItem>
            ))}
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Typography variant="body2" color="text.secondary">Всего: {total}</Typography>
        </Stack>
      </Paper>

      <Paper sx={{ overflow: 'hidden' }}>
        {loading && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        )}
        {!loading && (
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Тип</TableCell>
                  <TableCell>МП</TableCell>
                  <TableCell>SKU/арт.</TableCell>
                  <TableCell>Текущее → Новое</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell>Verifier</TableCell>
                  <TableCell>Приоритет</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell>Истекает</TableCell>
                  <TableCell align="right">Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={11} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      Нет действий с такими фильтрами
                    </TableCell>
                  </TableRow>
                )}
                {tableRows.map((row) => {
                  const payload = row.payload || {};
                  const sku = pickSku(payload);
                  const reason = payload.reason || '';
                  const cur = payload.current_value;
                  const next = payload.new_value;
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell><ActionPhoto marketplace={row.marketplace} payload={payload} /></TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={ACTION_TYPE_LABELS[row.action_type] || row.action_type}
                          sx={{
                            bgcolor: ACTION_TYPE_COLORS[row.action_type] || '#616161',
                            color: '#fff',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={MP_LABELS[row.marketplace] || row.marketplace} />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{sku || '—'}</TableCell>
                      <TableCell>
                        {cur != null || next != null ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>{formatNumber(cur)}</Typography>
                            <Typography variant="body2">→</Typography>
                            <Typography variant="body2" fontWeight={600}>{formatNumber(next)}</Typography>
                          </Box>
                        ) : '—'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Tooltip title={reason || ''}>
                          <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {reason || '—'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <VerifierChip status={row.verifier_status} warnings={row.verifier_warnings} />
                      </TableCell>
                      <TableCell>
                        <Chip size="small" color={PRIORITY_COLORS[row.priority] || 'default'} label={PRIORITY_LABELS[row.priority] || row.priority} />
                      </TableCell>
                      <TableCell>
                        <Stack direction="column" spacing={0.5} alignItems="flex-start">
                          <Chip size="small" color={STATUS_COLORS[row.status] || 'default'} label={STATUS_LABELS[row.status] || row.status} />
                          {row.auto_executed ? (
                            <Tooltip title={`Auto-executed по правилу: ${row.auto_approve_rule || '?'}`}>
                              <Chip
                                size="small"
                                icon={<AutoIcon fontSize="small" />}
                                color="secondary"
                                variant="outlined"
                                label="auto"
                                sx={{ height: 18, fontSize: 10 }}
                              />
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>{formatCountdown(row.expires_at)}</TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          <Tooltip title="Подробнее">
                            <IconButton size="small" onClick={() => openDetail(row)}>
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {(row.status === 'executed' || row.status === 'measured') && row.auto_executed && ['price_update', 'ad_bid_update', 'ad_campaign_pause'].includes(row.action_type) && (
                            <Tooltip title="Откатить (восстановить старое значение)">
                              <IconButton size="small" color="warning" onClick={() => doRollback(row)}>
                                <RollbackIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {isPending(row) && (
                            <>
                              <Tooltip title="Approve">
                                <IconButton size="small" color="success" onClick={() => doApprove(row)}>
                                  <ApproveIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit & Approve">
                                <IconButton size="small" color="primary" onClick={() => handleEditApproveOpen(row)}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Reject">
                                <IconButton size="small" color="error" onClick={() => handleRejectOpen(row)}>
                                  <RejectIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Snooze">
                                <IconButton size="small" onClick={() => handleSnoozeOpen(row)}>
                                  <SnoozeIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Подробнее */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Action #{detail?.id ?? '…'} — детали</DialogTitle>
        <DialogContent dividers>
          {detailLoading && <CircularProgress />}
          {detail && (
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip size="small" label={ACTION_TYPE_LABELS[detail.action_type] || detail.action_type} />
                <Chip size="small" variant="outlined" label={MP_LABELS[detail.marketplace] || detail.marketplace} />
                <Chip size="small" color={PRIORITY_COLORS[detail.priority] || 'default'} label={PRIORITY_LABELS[detail.priority] || detail.priority} />
                <Chip size="small" color={STATUS_COLORS[detail.status] || 'default'} label={STATUS_LABELS[detail.status] || detail.status} />
                <VerifierChip status={detail.verifier_status} warnings={detail.verifier_warnings} />
              </Stack>
              {detail.payload?.reason && (
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Reason</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{detail.payload.reason}</Typography>
                </Paper>
              )}

              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>Payload</AccordionSummary>
                <AccordionDetails>
                  <TextField
                    multiline fullWidth minRows={6} maxRows={20}
                    value={safeStringify(detail.payload)}
                    InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                  />
                </AccordionDetails>
              </Accordion>

              {Array.isArray(detail.payload?.sources) && detail.payload.sources.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>Источники ({detail.payload.sources.length})</AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      {detail.payload.sources.map((s, idx) => (
                        <Paper key={idx} variant="outlined" sx={{ p: 1 }}>
                          <Typography variant="caption" color="text.secondary">{s.type || 'source'}</Typography>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{s.ref}</Typography>
                          {s.confidence && <Chip size="small" label={`confidence: ${s.confidence}`} sx={{ mt: 0.5 }} />}
                        </Paper>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {Array.isArray(detail.verifier_warnings) && detail.verifier_warnings.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>Verifier warnings ({detail.verifier_warnings.length})</AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={1}>
                      {detail.verifier_warnings.map((w, idx) => (
                        <Paper key={idx} variant="outlined" sx={{ p: 1 }}>
                          <Typography variant="caption" color="warning.main">{w.severity}: {w.category}</Typography>
                          <Typography variant="body2">{w.description}</Typography>
                          {w.suggested_fix && (
                            <Typography variant="body2" color="text.secondary">fix: {w.suggested_fix}</Typography>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              )}

              {detail.effect_snapshot_before && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>Snapshot ДО</AccordionSummary>
                  <AccordionDetails>
                    <TextField
                      multiline fullWidth minRows={3} maxRows={15}
                      value={safeStringify(detail.effect_snapshot_before)}
                      InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                    />
                  </AccordionDetails>
                </Accordion>
              )}

              {detail.execution_result && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>Результат выполнения</AccordionSummary>
                  <AccordionDetails>
                    <TextField
                      multiline fullWidth minRows={3} maxRows={20}
                      value={safeStringify(detail.execution_result)}
                      InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: 12 } }}
                    />
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {detail && isPending(detail) && (
            <>
              <Button onClick={() => { handleEditApproveOpen(detail); }}>Edit & Approve</Button>
              <Button color="error" onClick={() => handleRejectOpen(detail)}>Reject</Button>
              <Button variant="contained" color="success" onClick={() => doApprove(detail).then(() => setDetailOpen(false))}>
                Approve
              </Button>
            </>
          )}
          <Button onClick={() => setDetailOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/* Edit & Approve */}
      <Dialog open={editPayloadOpen} onClose={() => setEditPayloadOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit & Approve — Action #{actionTarget?.id}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="caption" color="text.secondary">
            Отредактируйте JSON payload перед approve. Executor получит обновлённое значение.
          </Typography>
          <TextField
            multiline fullWidth minRows={10} maxRows={25}
            value={editPayloadText}
            onChange={(e) => setEditPayloadText(e.target.value)}
            InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
            sx={{ mt: 1 }}
          />
          <TextField
            fullWidth label="Комментарий (опционально)" sx={{ mt: 2 }}
            value={editComment}
            onChange={(e) => setEditComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPayloadOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleEditApproveSubmit}>Approve с правками</Button>
        </DialogActions>
      </Dialog>

      {/* Reject */}
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject — Action #{actionTarget?.id}</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Причина отклонения" multiline fullWidth minRows={3}
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>Отмена</Button>
          <Button variant="contained" color="error" onClick={handleRejectSubmit}>Reject</Button>
        </DialogActions>
      </Dialog>

      {/* Snooze */}
      <Dialog open={snoozeOpen} onClose={() => setSnoozeOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Snooze — Action #{actionTarget?.id}</DialogTitle>
        <DialogContent dividers>
          <TextField
            type="number" label="На сколько дней продлить" fullWidth
            value={snoozeDays}
            onChange={(e) => setSnoozeDays(e.target.value)}
            inputProps={{ min: 1, max: 30 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSnoozeOpen(false)}>Отмена</Button>
          <Button variant="contained" onClick={handleSnoozeSubmit}>Отложить</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
